import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router'

/* ---------- SEO ---------- */
export function Seo({ title, description }: { title: string; description: string }) {
  useEffect(() => {
    document.title = title
    const set = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector)
      if (el) el.setAttribute(attr, value)
    }
    set('meta[name="description"]', 'content', description)
    set('meta[property="og:title"]', 'content', title)
    set('meta[property="og:description"]', 'content', description)
  }, [title, description])
  return null
}

/* ---------- Scroll restore on route change ---------- */
export function ScrollToTop() {
  const { pathname, hash, key } = useLocation()
  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
    if (hash) {
      const id = hash.replace(/^#/, '')
      // Defer hash targets until the destination route paints.
      const jump = () => {
        const target = id ? document.getElementById(id) || document.querySelector(hash) : null
        if (target) target.scrollIntoView({ block: 'start' })
        else window.scrollTo(0, 0)
      }
      jump()
      const t = window.setTimeout(jump, 0)
      return () => window.clearTimeout(t)
    }
    const toTop = () => {
      window.scrollTo(0, 0)
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }
    toTop()
    const raf = window.requestAnimationFrame(toTop)
    const t = window.setTimeout(toTop, 0)
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearTimeout(t)
    }
  }, [pathname, hash, key])
  return null
}

/* ---------- Reveal on scroll (reduced-motion safe) ---------- */
export function Reveal({
  children,
  className = '',
  delay = 0,
}: {
  children: ReactNode
  className?: string
  delay?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true)
            io.disconnect()
          }
        })
      },
      { threshold: 0.12 },
    )
    io.observe(node)
    return () => io.disconnect()
  }, [])
  return (
    <div
      ref={ref}
      className={`reveal w-full ${visible ? 'is-visible' : ''} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  )
}

/* ---------- Section scaffolding ---------- */
export function Section({
  id,
  children,
  className = '',
  labelledBy,
}: {
  id?: string
  children: ReactNode
  className?: string
  labelledBy?: string
}) {
  return (
    <section id={id} aria-labelledby={labelledBy} className={`section-rule ${className}`}>
      <div className="mx-auto w-full max-w-7xl px-5 py-20 text-center sm:px-8 md:py-28">
        {children}
      </div>
    </section>
  )
}

export function SectionHeading({
  eyebrow,
  title,
  copy,
  id,
  eyebrowTone = 'mute',
}: {
  eyebrow?: string
  title: string
  copy?: string
  id?: string
  eyebrowTone?: 'signal' | 'mute' | 'nex'
}) {
  const eyebrowClass =
    eyebrowTone === 'nex' ? 'eyebrow-nex' : eyebrowTone === 'signal' ? 'eyebrow' : 'eyebrow-mute'
  return (
    <header className="mx-auto w-full max-w-4xl text-center">
      {eyebrow ? <p className={`${eyebrowClass} text-center`}>{eyebrow}</p> : null}
      <h2
        id={id}
        className={`${eyebrow ? 'mt-4' : ''} text-center text-3xl font-extrabold leading-[1.08] tracking-tight text-ink sm:text-4xl md:text-[2.75rem]`}
      >
        {title}
      </h2>
      {copy ? (
        <p className="mx-auto mt-5 max-w-2xl text-center text-base leading-relaxed text-ink-mute md:text-lg">
          {copy}
        </p>
      ) : null}
    </header>
  )
}

export function PrimaryCta({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="btn-primary">
      {children}
      <span aria-hidden="true">→</span>
    </Link>
  )
}

export function SecondaryCta({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="btn-secondary">
      {children}
    </Link>
  )
}

/* ---------- Sys divider, mono tag + running-light hairline ---------- */
export function SysDivider({ tag }: { tag: string }) {
  return (
    <div className="sys-divider" aria-hidden="true">
      <div className="mx-auto flex w-full max-w-7xl items-center gap-5 px-5 sm:px-8">
        <span className="font-mono text-[10px] font-medium uppercase tracking-[0.24em] text-signal">
          {tag}
        </span>
        <span className="bar" />
      </div>
    </div>
  )
}

/* ---------- Product shot frame, corner brackets + scanline ---------- */
export function ShotFrame({
  src,
  srcSet,
  sizes,
  width,
  height,
  alt,
  caption,
  tag,
  eager = false,
}: {
  src: string
  srcSet?: string
  sizes?: string
  width: number
  height: number
  alt: string
  caption?: string
  tag?: string
  eager?: boolean
}) {
  return (
    <figure>
      <div className="shot-frame rounded-sm">
        <span aria-hidden="true" className="shot-corner tl" />
        <span aria-hidden="true" className="shot-corner tr" />
        <span aria-hidden="true" className="shot-corner bl" />
        <span aria-hidden="true" className="shot-corner br" />
        <img
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          width={width}
          height={height}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          fetchPriority={eager ? 'high' : 'auto'}
          decoding="async"
          className="block h-auto w-full rounded-sm"
        />
        <span aria-hidden="true" className="scanline rounded-sm" />
        {tag ? (
          <span className="absolute bottom-3 left-3">
            <span className="illustrative-tag">{tag}</span>
          </span>
        ) : null}
      </div>
      {caption ? (
        <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em] text-ink-dim">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  )
}

/* ---------- Demo surface frame, honest interactive diagrams ---------- */
export function DemoFrame({
  title,
  children,
  tag = 'Conceptual diagram · not product UI',
  tone = 'signal',
  ariaLabel,
}: {
  title: string
  children: ReactNode
  tag?: string
  tone?: 'signal' | 'nex'
  ariaLabel?: string
}) {
  return (
    <figure
      className="relative overflow-hidden rounded-sm border border-base-line bg-base-900 shadow-surface"
      role="group"
      aria-label={ariaLabel ?? `${title}: conceptual diagram, not product UI`}
    >
      <div className="flex items-center gap-3 border-b border-base-line bg-base-850/80 px-4 py-2.5">
        <span
          className={`h-px w-6 ${tone === 'nex' ? 'bg-nex' : 'bg-signal'}`}
          aria-hidden="true"
        />
        <span className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-ink-dim">
          {title}
        </span>
      </div>
      <div className="relative">{children}</div>
      <figcaption className="absolute bottom-3 left-3">
        <span className="illustrative-tag">{tag}</span>
      </figcaption>
    </figure>
  )
}

/* ---------- Connected chain rail ---------- */
export function ChainRail({
  items,
  ariaLabel,
  accent = 'signal',
}: {
  items: string[]
  ariaLabel: string
  accent?: 'signal' | 'nex'
}) {
  return (
    <ol
      aria-label={ariaLabel}
      className="mx-auto flex w-full max-w-4xl flex-wrap items-center justify-center gap-y-3"
    >
      {items.map((item, i) => (
        <li key={item} className="flex items-center">
          <span
            className={`rounded-sm border bg-base-850 px-3 py-2 font-mono text-[11px] font-medium uppercase tracking-[0.1em] ${
              accent === 'nex'
                ? 'border-nex/40 text-nex-soft'
                : 'border-base-linebright text-ink'
            }`}
          >
            {item}
          </span>
          {i < items.length - 1 && (
            <span
              aria-hidden="true"
              className={`mx-1.5 ${accent === 'nex' ? 'text-nex/70' : 'text-signal/70'}`}
            >
              →
            </span>
          )}
        </li>
      ))}
    </ol>
  )
}

/* ---------- Trust bar, live-site marks, carried verbatim (Flag F1) ---------- */
const TRUST_MARKS = ['SOC 2 Type 2', 'HIPAA + BAA', 'PCI-DSS', 'CCPA', 'TCPA/DNC', 'Native e-sign']

export function TrustBar() {
  return (
    <div className="border-y border-base-line bg-base-900/60">
      <ul
        aria-label="Trust and compliance marks"
        className="flex w-full flex-wrap items-center justify-evenly gap-y-3 px-4 py-4 text-center sm:px-8"
      >
        {TRUST_MARKS.map((mark) => (
          <li key={mark} className="shrink-0">
            <Link
              to="/security"
              className="inline-block font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-ink-mute transition-colors duration-state hover:text-signal"
            >
              {mark}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ---------- Ticker, real product vocabulary, no stats ---------- */
const TICKER_TERMS = [
  'Hoppers',
  'Queues',
  'Dispositions',
  'Workflow versions',
  'Source attribution',
  'Consent state',
  'Calling windows',
  'Audit evidence',
  'Carrier mesh',
  'Whisper coaching',
  'Callback scheduling',
  'Protected-field masking',
]

export function Ticker() {
  const terms = [...TICKER_TERMS, ...TICKER_TERMS]
  return (
    <div className="ticker" aria-hidden="true">
      <div className="ticker-track">
        {terms.map((term, i) => (
          <span
            key={`${term}-${i}`}
            className="inline-flex items-center gap-4 whitespace-nowrap font-mono text-[11px] font-medium uppercase tracking-[0.24em] text-ink-mute"
          >
            <span className="text-[8px] text-signal">◆</span>
            {term}
          </span>
        ))}
      </div>
    </div>
  )
}
